import { useCallback, useEffect, useRef } from 'react';

import type { PusherFallbackOptions, PusherServiceInterface } from './use-label-batch-progress';
import type { LtlQuote } from '../../types/ltl';
import type { MutableRefObject } from 'react';

export type FreightQuotePusherMessage = {
  messageGuid: string;
  data: { errorMessage?: string; Quotes?: Record<string, unknown>[] };
};

export type UseFreightQuoteProgressOptions = {
  pusherService: PusherServiceInterface;
  pendingGuidsRef: MutableRefObject<Set<string>>;
  onQuotes: (quotes: LtlQuote[]) => void;
  onError: (message: string) => void;
  onComplete: () => void;
  timeoutMs?: number;
};

export type UseFreightQuoteProgressReturn = {
  startListening: (jobGuids: string[]) => void;
  cancelListening: () => void;
};

export const mapPascalCaseQuoteOptions = (raw: Record<string, unknown>[]): LtlQuote[] =>
  (raw ?? []).map((q) => ({
    offerId: (q.OfferId as string) ?? '',
    productTransactionId: (q.ProductTransactionId as string) ?? '',
    carrierName: (q.CarrierName as string) ?? '',
    carrierCode: (q.CarrierScac as string) ?? '',
    serviceName: (q.ServiceName as string) ?? '',
    totalCharges: (q.TotalCharges as number) ?? 0,
    transitDays: (q.TransitDays as number) ?? 0,
    estimatedDeliveryDate: (q.EstimatedDeliveryDate as string) ?? '',
    isGuaranteed: (q.IsGuaranteed as boolean) ?? false,
    quoteExpirationDate: (q.QuoteExpirationDate as string) ?? null,
    serviceType: (q.ServiceType as string) ?? null,
    originTerminalCode: (q.OriginTerminalCode as string) ?? null,
    destinationTerminalCode: (q.DestinationTerminalCode as string) ?? null,
    insuredAmount: (q.InsuredAmount as number) ?? null,
    insurancePremium: (q.InsurancePremium as number) ?? null,
    insuranceCertificateNumber: (q.InsuranceCertificateNumber as string) ?? null,
  }));

const TIMEOUT_MESSAGE = 'Quote request timed out. Please try again.';
const DEFAULT_TIMEOUT_MS = 30000;

export const useFreightQuoteProgress = ({
  pusherService,
  pendingGuidsRef,
  onQuotes,
  onError,
  onComplete,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseFreightQuoteProgressOptions): UseFreightQuoteProgressReturn => {
  const onQuotesRef = useRef(onQuotes);
  onQuotesRef.current = onQuotes;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const handleMessage = useCallback((message: FreightQuotePusherMessage) => {
    const { messageGuid, data } = message;
    if (!pendingGuidsRef.current.has(messageGuid)) return;
    pendingGuidsRef.current.delete(messageGuid);

    if (data?.errorMessage) {
      onErrorRef.current(data.errorMessage);
    } else {
      const quotes = mapPascalCaseQuoteOptions((data?.Quotes ?? []) as Record<string, unknown>[]);
      onQuotesRef.current(quotes);
    }

    if (pendingGuidsRef.current.size === 0) {
      onCompleteRef.current();
    }
    // pendingGuidsRef is a stable ref — .current is always fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fallbackOptions: PusherFallbackOptions = {
      timeoutMs,
      onTimeout: () => {
        if (pendingGuidsRef.current.size === 0) return;
        pendingGuidsRef.current.clear();
        onErrorRef.current(TIMEOUT_MESSAGE);
        onCompleteRef.current();
      },
    };

    const unsubscribe = pusherService.subscribe<FreightQuotePusherMessage>('freight-quote', handleMessage, fallbackOptions);

    return unsubscribe;
    // pendingGuidsRef is a stable ref — .current is always fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pusherService, timeoutMs, handleMessage]);

  const startListening = useCallback((jobGuids: string[]) => {
    pendingGuidsRef.current.clear();
    jobGuids.forEach((guid) => pendingGuidsRef.current.add(guid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelListening = useCallback(() => {
    pendingGuidsRef.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { startListening, cancelListening };
};
